import React from 'react';
import I18nContext from '../../i18n/I18nContext';
import PageEmptyState from '../PageEmptyState/PageEmptyState';
import './FormErrorBoundary.css';

/**
 * Error Boundary for form pages (spec §3.12): catches unexpected errors during rendering and
 * shows the app's error state — the warning icon, "Xatolik yuz berdi", an ink "Qaytadan" button.
 * The error's own text is never shown to the user (development builds add a collapsed detail).
 */
class FormErrorBoundary extends React.Component {
  static contextType = I18nContext;

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Form error caught by boundary:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Log error to console for debugging
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      const t = this.context?.t || ((k) => k);
      return (
        <div className="feb">
          <PageEmptyState
            variant="error"
            title={t('ui.errorTitle')}
            description={t('ui.unexpectedRequestError')}
            onRetry={this.handleRetry}
            secondaryLabel={this.props.onClose ? t('ui.close') : undefined}
            onSecondaryAction={this.props.onClose}
          >
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="feb-details">
                <summary>{t('ui.errorDetailsDev')}</summary>
                <pre>{this.state.error.toString()}</pre>
                <pre>{this.state.errorInfo?.componentStack}</pre>
              </details>
            )}
            {this.state.retryCount > 0 && (
              <p className="feb-retry-info">
                {t('ui.retryAttempt', { count: this.state.retryCount })}
              </p>
            )}
          </PageEmptyState>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FormErrorBoundary;
